
import React from 'react';
import { Target, Eye, Award, History as HistoryIcon, Shield, Users, Building2 } from 'lucide-react';
import { AboutSettings } from '../types';

interface AboutSectionProps {
  settings: AboutSettings;
}

const AboutSection: React.FC<AboutSectionProps> = ({ settings }) => {
  return (
    <div className="bg-white dark:bg-slate-950 transition-colors">
      {/* Company Profile Section */}
      <section className="py-24 px-4 bg-slate-50 dark:bg-slate-900/30">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 md:order-1">
            <div className="aspect-video rounded-[3rem] overflow-hidden shadow-2xl">
              <img 
                src={settings.profileImage} 
                alt="Company Profile" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="space-y-6 order-1 md:order-2">
            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-black text-xs uppercase tracking-[0.3em]">
              <Building2 size={16} />
              Corporate Identity
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white uppercase tracking-tighter leading-none">
              {settings.profileTitle}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
              {settings.profileDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Foundation Section */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-black text-xs uppercase tracking-[0.3em]">
              <HistoryIcon size={16} />
              Our Foundation
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
              {settings.foundationTitle}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              {settings.foundationDescription}
            </p>
            <div className="grid grid-cols-2 gap-6 pt-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
                <div className="text-2xl font-black text-blue-900 dark:text-blue-400">25+</div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Years Experience</div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
                <div className="text-2xl font-black text-blue-900 dark:text-blue-400">500+</div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Projects Completed</div>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-square rounded-[3rem] overflow-hidden shadow-2xl">
              <img 
                src={settings.foundationImage} 
                alt="Foundation" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-blue-700 text-white p-8 rounded-3xl shadow-xl hidden md:block">
              <Award size={40} className="mb-4" />
              <div className="font-black text-xl uppercase tracking-tighter">Certified<br/>Quality</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900/50 transition-colors">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="bg-white dark:bg-slate-900 p-10 md:p-16 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm group hover:border-blue-500 transition-all duration-500">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center text-blue-700 dark:text-blue-400 mb-8 group-hover:scale-110 transition-transform">
                <Target size={32} />
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-6">{settings.missionTitle}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
                {settings.missionDescription}
              </p>
              <ul className="mt-8 space-y-4">
                {settings.missionPoints.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Vision */}
            <div className="bg-white dark:bg-slate-900 p-10 md:p-16 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm group hover:border-blue-500 transition-all duration-500">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center text-blue-700 dark:text-blue-400 mb-8 group-hover:scale-110 transition-transform">
                <Eye size={32} />
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-6">{settings.visionTitle}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
                {settings.visionDescription}
              </p>
              <ul className="mt-8 space-y-4">
                {settings.visionPoints.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto text-center mb-16">
          <h2 className="text-blue-800 dark:text-blue-400 font-black text-[10px] uppercase tracking-[0.4em] mb-4">Core Principles</h2>
          <h3 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white uppercase tracking-tighter">The Sai SSK <span className="text-blue-600">Standard</span></h3>
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { icon: <Shield />, title: 'Integrity', desc: 'We maintain the highest ethical standards in all our dealings.' },
            { icon: <Users />, title: 'Collaboration', desc: 'Working together with stakeholders to achieve common goals.' },
            { icon: <Award />, title: 'Excellence', desc: 'Striving for perfection in every asphalt layer and bridge support.' },
          ].map((value, i) => (
            <div key={i} className="p-10 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 text-center hover:bg-white dark:hover:bg-slate-800 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center text-blue-700 dark:text-blue-400 mx-auto mb-6">
                {value.icon}
              </div>
              <h4 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-4">{value.title}</h4>
              <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{value.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AboutSection;
